import { useState, useEffect, useCallback, useRef } from 'react';
import { TaskTimelineResponse } from '../types/timeline';
import { timelineApi } from '../services/timelineApi';

interface UseTaskTimelineOptions {
  taskId: number | string;
  enabled?: boolean;
  onLoad?: (data: TaskTimelineResponse) => void;
}

interface UseTaskTimelineResult {
  data: TaskTimelineResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTaskTimeline({
  taskId,
  enabled = true,
  onLoad,
}: UseTaskTimelineOptions): UseTaskTimelineResult {
  const [data, setData] = useState<TaskTimelineResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const onLoadRef = useRef(onLoad);

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  const fetchTimeline = useCallback(async () => {
    if (!taskId || !enabled) return;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await timelineApi.getTaskTimeline(taskId);
      if (reqId === requestIdRef.current) {
        setData(response);
        onLoadRef.current?.(response);
      }
    } catch (err) {
      if (reqId === requestIdRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load task timeline');
      }
    } finally {
      if (reqId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [taskId, enabled]);

  useEffect(() => {
    if (taskId && enabled) {
      fetchTimeline();
    }
  }, [taskId, enabled, fetchTimeline]);

  return { data, loading, error, refresh: fetchTimeline };
}
